import { Menu } from '@base-ui/react/menu'
import { cx } from '@/lib/cx'

export type TagMultiSelectOption = { id: number; label: string }

function tagSummary(selectedIds: number[], options: TagMultiSelectOption[]): string {
  if (selectedIds.length === 0) return 'All tags'
  if (selectedIds.length === 1) {
    return options.find((tag) => tag.id === selectedIds[0])?.label ?? '1 tag'
  }
  return `${selectedIds.length} tags`
}

// Checkbox multi-select over the tag vocabulary (OR semantics), styled as a
// staff-select trigger. Lives in the kit because it composes raw Base UI Menu
// primitives — the D1 guard keeps those out of feature code.
export function TagMultiSelect({
  selectedIds,
  options,
  onChange,
}: {
  selectedIds: number[]
  options: TagMultiSelectOption[]
  onChange: (tagIds: number[]) => void
}) {
  function toggle(tagId: number, checked: boolean) {
    onChange(checked ? [ ...selectedIds, tagId ] : selectedIds.filter((id) => id !== tagId))
  }

  return (
    <Menu.Root>
      <Menu.Trigger
        className={cx('staff-select staff-multi-select', selectedIds.length > 0 && 'is-active')}
        aria-label="Filter by tag"
      >
        <span className="staff-multi-select__label">{tagSummary(selectedIds, options)}</span>
        <span className="staff-multi-select__chevron" aria-hidden="true">
          ▾
        </span>
      </Menu.Trigger>
      <Menu.Portal>
        <Menu.Positioner sideOffset={6} align="start">
          <Menu.Popup className="menu-popup staff-multi-select__menu">
            {selectedIds.length > 0 && (
              <>
                <Menu.Item
                  className="menu-item staff-multi-select__clear"
                  onClick={() => onChange([])}
                >
                  Clear tags
                </Menu.Item>
                <Menu.Separator className="menu-separator" />
              </>
            )}
            {options.map((tag) => {
              const checked = selectedIds.includes(tag.id)
              return (
                <Menu.CheckboxItem
                  key={tag.id}
                  checked={checked}
                  onCheckedChange={(next) => toggle(tag.id, next)}
                  className="menu-item staff-multi-select__option"
                  label={tag.label}
                >
                  <span
                    className={cx('staff-multi-select__check', checked && 'is-checked')}
                    aria-hidden="true"
                  >
                    {checked ? '✓' : ''}
                  </span>
                  {tag.label}
                </Menu.CheckboxItem>
              )
            })}
          </Menu.Popup>
        </Menu.Positioner>
      </Menu.Portal>
    </Menu.Root>
  )
}
